import { CloudUploadOutlined, Download, EmailOutlined, Groups, MarkEmailRead, Pause, PlayArrow, Refresh, Visibility } from '@mui/icons-material';
import { Box, Chip, Grid, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';

import { LoadingButton } from '@mui/lab';
import { MUIDataTableOptions } from 'mui-datatables';
import { DateTimeFormat, getLocalDateTime } from 'src/common';
import WO2Button from 'src/common/components/button/Button';
import { ServerSideDataTable } from 'src/common/components/dataTable/serverSide';
import { DatatableColumn, FilterDataType } from 'src/common/components/dataTable/types';
import { useConfirmation } from 'src/common/components/dialogs';
import { QueryField } from 'src/store';
import { REPORT_STATUS_COLORS, REPORT_STATUS_NAMES } from '../../constants';
import { Props } from '../container';
import { BulkReportRecord } from '../store/types';
import { ReportingBatchRecordModal } from './ReportingBatchRecordModal';
import { ReportingBatchEmailPreviewModal } from './ReportingBatchEmailPreviewModal';

export interface ReportingBatchRecordsProps extends Props {
  getBulkProgress: () => void;
  initiateBulk: (currentPage: number, currentPageSize: number, allRecords: boolean, batchRecords?: number[]) => void;
  fetchBulkReportRecords: (page: number, pageSize: number, queryFields: QueryField[]) => void;
  terminateRecord: (currentPage: number, currentPageSize: number, recordIndex?: number) => void;
  approveScheduledInstanceEvent: (isApproved: boolean, isRejected: boolean, isRetry: boolean) => void;
  downloadCsvForBatchRecords: (selectedRows: number[], queryFields: QueryField[]) => void;
  refetchBulkReport: () => void;
  refreshing: boolean;
}

export const ReportingBatchRecords = (props: ReportingBatchRecordsProps) => {
  const confirm = useConfirmation();

  const [viewingRecord, setViewingRecord] = useState<BulkReportRecord | null>(null);
  const [emailPreviewRecord, setEmailPreviewRecord] = useState<BulkReportRecord | null>(null);
  const [selectedRecordIndices, setSelectedRecordIndices] = useState<number[]>([]);
  const [pagedRequest, setPagedRequest] = useState({
    pageNumber: 1,
    pageSize: 100,
    queryFields: [] as QueryField[],
  });

  useEffect(() => {
    props.fetchBulkReportRecords(pagedRequest.pageNumber - 1, pagedRequest.pageSize, pagedRequest.queryFields);
  }, [pagedRequest]);

  useEffect(() => {
    setSelectedRecordIndices([]);
  }, [pagedRequest.queryFields]);

  const downloadReport = (record: BulkReportRecord) => {
    if (!record.reportRequestId || !record.reportFileName) return;
    props.downloadReport({
      key: record.reportRequestId,
      clientId: record.clientOrGroupId,
      downloadFileName: record.reportFileName,
    });
  };

  const isDownloadAvailable = useMemo(() => {
    const requestIds = props.batch.paginatedReportRecords.results
      .filter((row: BulkReportRecord) => selectedRecordIndices.includes(row.recordIndex) && row.status === 'C')
      .map((row: BulkReportRecord) => (row.reportRequestId ? row.reportRequestId : ''))
      .filter((id) => id.length > 0);

    if (requestIds.length === 0) return;

    return true;
  }, [selectedRecordIndices, props.batch.paginatedReportRecords]);

  const downloadSelectedRecords = () => {
    const requestIds = props.batch.paginatedReportRecords.results
      .filter((row: BulkReportRecord) => selectedRecordIndices.includes(row.recordIndex) && row.status === 'C')
      .map((row: BulkReportRecord) => (row.reportRequestId ? row.reportRequestId : ''))
      .filter((id) => id.length > 0);

    if (requestIds.length === 0) return;

    props.downloadBulkReports({ requestIds: requestIds });
  };

  const reviewScheduledInstance = (row: BulkReportRecord, reviewStatus: string) => {
    props
      .reviewScheduledInstanceRecord({
        batchId: row.batchId,
        recordIndex: row.recordIndex,
        reviewStatus,
      })
      .then(() => props.fetchBulkReportRecords(pagedRequest.pageNumber - 1, pagedRequest.pageSize, pagedRequest.queryFields));
  };

  const columns: DatatableColumn[] = useMemo(
    () => [
      {
        name: 'clientOrGroupId',
        label: 'ID',
        options: { sort: true },
      },
      {
        name: 'clientName',
        label: 'Name',
        options: {
          sort: true,
          customBodyRenderLite: (dataIndex: number) => {
            const row = props.batch.paginatedReportRecords?.results[dataIndex];
            if (!row) return '';
            return (
              <Box display="flex" gap={1} alignItems="center">
                {row.isClientGroup && <Groups fontSize="small" color="primary" />}
                <Typography>{row.clientName}</Typography>
              </Box>
            );
          },
        },
      },
      { name: 'afslName', label: 'AFSL' },
      { name: 'practiceName', label: 'Practice' },
      { name: 'adviserName', label: 'Adviser' },
      { name: 'reportFileName', label: 'Filename' },
      {
        name: 'status',
        label: 'Status',
        filterDataType: FilterDataType.string,
        options: {
          customBodyRenderLite: (dataIndex: number) => {
            const row = props.batch.paginatedReportRecords?.results[dataIndex];
            if (!row) return '';
            const label = REPORT_STATUS_NAMES[row.status] ?? row.status;
            const color = REPORT_STATUS_COLORS[label];

            return (
              <Box display="flex" gap={1} alignItems="center">
                <Chip label={label} color={color} />
                {row.scheduledInstanceRecord?.reviewStatus === 'M' && (
                  <Tooltip title="Failed to send Email">
                    <EmailOutlined color="error" />
                  </Tooltip>
                )}
                {row.scheduledInstanceRecord?.reviewStatus === 'D' && (
                  <Tooltip title="Failed to upload Document">
                    <CloudUploadOutlined color="error" />
                  </Tooltip>
                )}
                {row.scheduledInstanceRecord?.reviewStatus === 'H' && <Chip label="Held" color="warning" />}
              </Box>
            );
          },
        },
      },
      {
        name: 'completedOn',
        label: 'Completed At',
        options: {
          customBodyRenderLite: (dataIndex: number) => {
            const row = props.batch.paginatedReportRecords?.results[dataIndex];
            return row?.completedOn ? getLocalDateTime(row.completedOn, DateTimeFormat.DateTimeShort) : '';
          },
        },
      },
      {
        name: 'emailSentOn',
        label: 'Sent/Uploaded At',
        options: {
          customBodyRenderLite: (dataIndex: number) => {
            const row = props.batch.paginatedReportRecords?.results[dataIndex];
            if (!row || !row.scheduledInstanceRecord) {
              return '';
            }
            const { emailSentOn, documentUploadedOn } = row.scheduledInstanceRecord;
            return (
              <Box display="flex" flexDirection="column">
                {emailSentOn && <Typography>Sent: {getLocalDateTime(emailSentOn, DateTimeFormat.DateTimeShort)}</Typography>}
                {documentUploadedOn && <Typography>Uploaded: {getLocalDateTime(documentUploadedOn, DateTimeFormat.DateTimeShort)}</Typography>}
              </Box>
            );
          },
        },
      },
      {
        name: 'createdOn',
        label: 'Created On',
        options: {
          customBodyRenderLite: (dataIndex: number) => {
            const row = props.batch.paginatedReportRecords?.results[dataIndex];
            return row?.createdOn ? getLocalDateTime(row.createdOn, DateTimeFormat.DateTimeShort) : '';
          },
        },
      },
      {
        name: '',
        label: '',
        options: {
          sort: false,
          filter: false,
          customBodyRenderLite: (dataIndex: number) => {
            const row = props.batch.paginatedReportRecords?.results[dataIndex];
            if (!row) return null;
            const inPendingReview = props.batch.bulkReport.scheduleInstanceDetails?.reviewStatus === 'P';

            return (
              <Box display="flex">
                <Tooltip title="Download">
                  <span>
                    <IconButton color="primary" disabled={row.status !== 'C'} onClick={() => downloadReport(row)}>
                      <Download />
                    </IconButton>
                  </span>
                </Tooltip>

                {row.scheduledInstanceRecord && (
                  <>
                    {row.scheduledInstanceRecord.reviewStatus === 'A' ? (
                      <Tooltip title="Hold">
                        <span>
                          <IconButton
                            disabled={
                              row.status === 'P' ||
                              row.scheduledInstanceRecord?.reviewStatus !== 'A' ||
                              !inPendingReview ||
                              props.reviewScheduledInstanceRecordProgress.isLoading
                            }
                            color="primary"
                            onClick={() => reviewScheduledInstance(row, 'H')}
                          >
                            <Pause />
                          </IconButton>
                        </span>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Activate">
                        <span>
                          <IconButton
                            color="primary"
                            disabled={
                              row.status === 'P' ||
                              row.scheduledInstanceRecord?.reviewStatus !== 'H' ||
                              !inPendingReview ||
                              props.reviewScheduledInstanceRecordProgress.isLoading
                            }
                            onClick={() => reviewScheduledInstance(row, 'A')}
                          >
                            <PlayArrow />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </>
                )}

                <Tooltip title="View">
                  <IconButton color="primary" onClick={() => setViewingRecord(row)}>
                    <Visibility />
                  </IconButton>
                </Tooltip>
                {props.batch.bulkReport.emailParameters && (
                  <Tooltip title="Email Preview">
                    <IconButton color="primary" onClick={() => setEmailPreviewRecord(row)}>
                      <MarkEmailRead />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            );
          },
        },
      },
    ],
    [props.batch.bulkReport.scheduleInstanceDetails, props.batch.paginatedReportRecords]
  );

  const options: MUIDataTableOptions = {
    filter: true,
    search: false,
    viewColumns: false,
    download: true,
    print: false,
    selectableRows: 'multiple',
    selectableRowsHideCheckboxes: false,
    pagination: true,
    serverSide: true,
    rowsSelected: props.batch.paginatedReportRecords?.results
      ?.map((row, index) => (selectedRecordIndices.includes(row.recordIndex) ? index : -1))
      .filter((index) => index !== -1),
    onRowSelectionChange: (_currentRowsSelected, _allRowsSelected, rowsSelected) => {
      const currentPageResults = props.batch.paginatedReportRecords?.results ?? [];
      const selected = rowsSelected ?? [];
      const currentPageRecordIndices = selected
        .map((displayIndex) => currentPageResults[displayIndex]?.recordIndex)
        .filter((index): index is number => index !== undefined);
      const currentPageAllRecordIndices = currentPageResults.map((r) => r.recordIndex);
      const otherPageSelections = selectedRecordIndices.filter((ri) => !currentPageAllRecordIndices.includes(ri));
      setSelectedRecordIndices([...otherPageSelections, ...currentPageRecordIndices]);
    },
    customToolbar: () => (
      <>
        <Tooltip title="Refresh">
          <IconButton
            sx={{ mr: 1 }}
            size="small"
            disabled={props.fetchBulkReportRecordListProgress.isLoading}
            onClick={() => {
              props.fetchBulkReportRecords(pagedRequest.pageNumber - 1, pagedRequest.pageSize, pagedRequest.queryFields);
              props.getBulkProgress();

              if (
                props.batch.batchProgress &&
                props.batch.bulkReport.scheduleInstanceDetails &&
                (props.batch.bulkReport.scheduleInstanceDetails.reviewStatus === 'P' ||
                  props.batch.bulkReport.scheduleInstanceDetails.reviewStatus === 'A')
              ) {
                const completedRecords = props.batch.batchProgress?.completedRecords ?? 0;
                const totalRecords = props.batch.batchProgress?.totalRecords ?? 0;
                if (completedRecords >= totalRecords) {
                  props.refetchBulkReport();
                }
              }
            }}
          >
            <Refresh />
          </IconButton>
        </Tooltip>
        <WO2Button size="small" variant="contained" disabled={!isDownloadAvailable} onClick={downloadSelectedRecords} sx={{ mr: 1 }}>
          Download
        </WO2Button>

        {props.batch.bulkReport.scheduleInstanceDetails?.reviewStatus !== 'A' &&
          props.batch.bulkReport.scheduleInstanceDetails?.reviewStatus !== 'R' && (
            <LoadingButton
              loading={props.initiateBulkRequestProgress.isLoading || props.refreshing}
              size="small"
              variant="contained"
              onClick={() => props.initiateBulk(pagedRequest.pageNumber - 1, pagedRequest.pageSize, selectedRecordIndices.length === 0, selectedRecordIndices)}
              sx={{ mr: 1 }}
            >
              Regenerate
            </LoadingButton>
          )}

        {props.batch.bulkReport.scheduleInstanceDetails && props.batch.batchProgress && (
          <>
            {props.batch.bulkReport.scheduleInstanceDetails?.reviewStatus === 'P' && (props.batch.batchProgress?.isReadyForReview ?? false) !== true && (
              <LoadingButton
                loading={props.terminateBulkRequestProgress.isLoading || props.refreshing}
                color="warning"
                variant="contained"
                sx={{ mr: 1 }}
                onClick={() => {
                  confirm({
                    title: 'Cancel All In Progress',
                    description: 'Proceeding with Cancel All will stop all In Progress reports and reset them to Not Started',
                    description2: 'Reports that have already Completed or are in Error will stay as they are.',
                  }).then(() => {
                    props.terminateRecord(pagedRequest.pageNumber - 1, pagedRequest.pageSize);
                  });
                }}
              >
                Cancel All
              </LoadingButton>
            )}
          </>
        )}

        {props.batch.bulkReport.scheduleInstanceDetails && props.batch.batchProgress && (
          <>
            {props.batch.bulkReport.scheduleInstanceDetails?.reviewStatus === 'P' && (props.batch.batchProgress?.isReadyForReview ?? false) && (
              <LoadingButton
                sx={{ mr: 1 }}
                loading={props.approveScheduledInstanceProgress.isLoading}
                variant="contained"
                onClick={() => props.approveScheduledInstanceEvent(false, true, false)}
              >
                Reject
              </LoadingButton>
            )}
            {props.batch.bulkReport.scheduleInstanceDetails?.reviewStatus === 'P' && (props.batch.batchProgress?.isReadyForReview ?? false) && (
              <LoadingButton
                sx={{ mr: 1 }}
                loading={props.approveScheduledInstanceProgress.isLoading}
                variant="contained"
                onClick={() => props.approveScheduledInstanceEvent(true, false, false)}
              >
                Approve
              </LoadingButton>
            )}

            {props.batch.bulkReport.scheduleInstanceDetails?.reviewStatus === 'A' &&
              (props.batch.bulkReport.scheduleInstanceDetails?.failedEmailSentRecords > 0 ||
                props.batch.bulkReport.scheduleInstanceDetails?.failedDocumentUploadRecords > 0) && (
                <LoadingButton
                  loading={props.approveScheduledInstanceProgress.isLoading}
                  variant="contained"
                  onClick={() => props.approveScheduledInstanceEvent(false, false, true)}
                >
                  Retry Process
                </LoadingButton>
              )}
          </>
        )}
      </>
    ),
  };

  return (
    <Grid container direction="column" spacing={2}>
      <Grid item>
        <Paper elevation={4}>
          <ServerSideDataTable<BulkReportRecord>
            pagedRequest={pagedRequest}
            pagedResult={props.batch.paginatedReportRecords}
            loadingProgress={props.fetchBulkReportRecordListProgress}
            columns={columns}
            options={options}
            handlePagedRequest={setPagedRequest}
            csvDownload={() => {
              props.downloadCsvForBatchRecords(selectedRecordIndices, pagedRequest.queryFields);
            }}
            title={
              props.batch.batchProgress
                ? `Completed ${props.batch.batchProgress?.completedRecords} out of ${props.batch.batchProgress?.totalRecords}`
                : ''
            }
          />
        </Paper>
      </Grid>

      {emailPreviewRecord && (
        <ReportingBatchEmailPreviewModal
          record={emailPreviewRecord}
          isOpen={true}
          handleClose={() => setEmailPreviewRecord(null)}
          fetchEmailPreview={props.fetchEmailPreview}
          emailPreview={props.batch.emailPreview}
        />
      )}

      {viewingRecord && (
        <ReportingBatchRecordModal
          currentPage={pagedRequest.pageNumber - 1}
          currentPageSize={pagedRequest.pageSize}
          reportParameters={props.batch.bulkReport.reportParameters}
          record={viewingRecord}
          terminateRecord={props.terminateRecord}
          isOpen={true}
          handleClose={() => setViewingRecord(null)}
          downloadReport={downloadReport}
          fetchGroupReport={props.fetchGroupReport}
          groupReport={props.batch.groupReport}
          terminateRecordProgress={props.terminateBulkRequestProgress}
          hasClientEditAdminOnly={props.hasClientEditAdminOnly}
        />
      )}
    </Grid>
  );
};
